import { useMemo, useState } from "react";
import { useParties, usePartyLinks } from "../hooks/useDirectory";
import { useTasks } from "../hooks/useTasks";
import { useUiStore } from "../state/uiStore";
import { Avatar } from "../components/Avatar";
import { TaskRow } from "../components/TaskRow";
import { taskToVM } from "../lib/taskVM";
import { useLists, useCompleteTask, useUpdateTask } from "../hooks/useTasks";

/** Contacts CRM (lecture seule, DT F-11) : Party concernée par des tâches
 * (context_type crm.*), personnes liées via relations/rôles. */
export function ContactsScreen() {
  const [q, setQ] = useState("");
  const parties = useParties(q);
  const tasks = useTasks();
  const lists = useLists();
  const completeTask = useCompleteTask();
  const updateTask = useUpdateTask();
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null);
  const links = usePartyLinks(selectedPartyId);
  const { selectedTaskId, selectTask } = useUiStore();

  const tasksByParty = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tasks.data ?? []) {
      if (t.context_type?.startsWith("crm.") && t.context_id) {
        m.set(t.context_id, (m.get(t.context_id) ?? 0) + 1);
      }
    }
    return m;
  }, [tasks.data]);

  const partyTasks = useMemo(
    () =>
      (tasks.data ?? []).filter(
        (t) =>
          t.context_id === selectedPartyId &&
          t.context_type?.startsWith("crm."),
      ),
    [tasks.data, selectedPartyId],
  );

  const selectedParty = parties.data?.find((p) => p.id === selectedPartyId);

  return (
    <>
      <div className="view-header">
        <div className="view-title">Contacts</div>
        <div className="view-caption">Référentiel CRM · lecture seule</div>
      </div>
      <div
        className="main-scroll"
        style={{ display: "flex", gap: 28, paddingTop: 16 }}
      >
        <div style={{ flex: "0 0 340px" }}>
          <div className="quick-add">
            <span style={{ color: "var(--ink-ghost)" }}>⌕</span>
            <input
              value={q}
              placeholder="Rechercher un contact…"
              onChange={(e) => setQ(e.target.value)}
              aria-label="Recherche contact"
            />
          </div>
          {parties.isError && (
            <div className="empty-state">
              CRM indisponible — le module n'est pas déployé ou vos scopes CRM
              read manquent.
            </div>
          )}
          {parties.isLoading && <div className="empty-state">Chargement…</div>}
          <div style={{ marginTop: 10 }}>
            {(parties.data ?? []).map((p) => (
              <button
                key={p.id}
                className={`task-row${p.id === selectedPartyId ? " selected" : ""}`}
                style={{ width: "100%" }}
                onClick={() =>
                  setSelectedPartyId(p.id === selectedPartyId ? null : p.id)
                }
              >
                <Avatar name={p.display_name} size={22} />
                <span className="task-title" style={{ fontWeight: 600 }}>
                  {p.display_name}
                </span>
                <span className="tag">
                  {p.party_type === "person" ? "Personne" : "Organisation"}
                </span>
                {tasksByParty.get(p.id) ? (
                  <span className="nav-count" style={{ marginLeft: "auto" }}>
                    {tasksByParty.get(p.id)}
                  </span>
                ) : null}
              </button>
            ))}
            {parties.data?.length === 0 && !parties.isLoading && (
              <div className="empty-state">Aucune correspondance.</div>
            )}
          </div>
        </div>

        {selectedParty && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={selectedParty.display_name} size={30} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {selectedParty.display_name}
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>
                  Contact · CRM — lecture seule
                </div>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">Personnes liées</div>
              {links.isLoading && (
                <div className="empty-state">Chargement…</div>
              )}
              {(links.data?.relationships ?? []).map((r) => (
                <div key={r.id} className="prop-row">
                  <span className="prop-label">{r.relationship_type_code}</span>
                  <span className="prop-value mono" style={{ fontSize: 11 }}>
                    {r.party_a_id === selectedParty.id
                      ? r.party_b_id
                      : r.party_a_id}
                  </span>
                </div>
              ))}
              {(links.data?.roles ?? []).map((r) => (
                <div key={r.id} className="prop-row">
                  <span className="prop-label">{r.work_code}</span>
                  <span className="prop-value mono" style={{ fontSize: 11 }}>
                    {r.person_party_id === selectedParty.id
                      ? r.organization_party_id
                      : r.person_party_id}
                  </span>
                </div>
              ))}
              {links.data &&
                links.data.relationships.length + links.data.roles.length ===
                  0 && (
                  <div className="empty-state">
                    Aucune relation enregistrée.
                  </div>
                )}
            </div>

            <div className="detail-section">
              <div className="detail-section-title">Tâches liées</div>
              {partyTasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={taskToVM(t, lists.data)}
                  selected={t.id === selectedTaskId}
                  onSelect={(id) =>
                    selectTask(id === selectedTaskId ? null : id)
                  }
                  onToggleDone={(vm) =>
                    vm.status === "done"
                      ? updateTask.mutate({
                          id: vm.id,
                          body: { status: "todo" },
                        })
                      : completeTask.mutate(vm.id)
                  }
                />
              ))}
              {partyTasks.length === 0 && (
                <div className="empty-state">
                  Aucune tâche rattachée à ce contact.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
